import type { Channel } from '@nexo/types';
import { Avatar, Button, IconButton } from '@nexo/ui';
import { Mic, Settings, Video, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam } from '../../layouts/TeamContext';
import { useCallsConfig } from '../../lib/queries';
import { useChannelCall, useStartCall } from './CallControls';
import { callSlot, callStore, useCall } from './callStore';
import { canIn } from '../../lib/permissions';
import { ChannelSettingsDialog } from '../channels/ChannelSettings';

/**
 * A Discord-style voice channel: a room people drop into with microphone and, optionally,
 * camera. Once joined, the live call is docked into this view (see `callSlot`).
 */
export function VoiceChannelView({ channel }: { channel: Channel }) {
  const { online } = useTeam();
  const config = useCallsConfig();
  const current = useCall();
  const live = useChannelCall(channel.id);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const inCall = current?.join.channelId === channel.id;
  const { t } = useTranslation();

  useEffect(() => {
    if (!slot) return;
    callSlot.set({ channelId: channel.id, element: slot });
    return () => {
      callSlot.set(null);
      // Leaving the page keeps the call going in the compact bar.
      if (callStore.current()?.join.channelId === channel.id) callStore.setExpanded(false);
    };
  }, [slot, channel.id]);

  const startCall = useStartCall();
  const join = (video: boolean) => startCall(channel.id, video);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <>
      <TeamHeader>
        <Volume2 className="size-[18px] shrink-0 text-muted" />
        <h1 className="shrink-0 truncate text-[17px] font-semibold tracking-tight">
          {channel.name}
        </h1>
        {channel.description && (
          <p className="min-w-0 truncate text-[13px] text-muted max-sm:hidden">
            {channel.description}
          </p>
        )}
        {canIn(channel, 'manage_channels') && (
          <IconButton
            label={t('Ajustes del canal')}
            className="ml-auto"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings />
          </IconButton>
        )}
        {settingsOpen && (
          <ChannelSettingsDialog
            channel={channel}
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
        )}
      </TeamHeader>
      <div ref={setSlot} className="relative m-2 flex min-h-0 flex-1 md:m-3">
        {!inCall && (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 rounded-2xl bg-surface-2 p-6 text-center">
            <span className="flex size-16 items-center justify-center rounded-full bg-accent-soft text-accent">
              <Volume2 className="size-8" />
            </span>
            <div>
              <h2 className="text-xl font-semibold">{channel.name}</h2>
              <p className="mt-1 text-sm text-muted">
                {live
                  ? live.participants.length === 1
                    ? t('Hay 1 persona en el canal')
                    : t('Hay {{count}} personas en el canal', { count: live.participants.length })
                  : t('No hay nadie todavía. Entra y los demás podrán unirse cuando quieran.')}
              </p>
            </div>
            {live && (
              <ul className="flex flex-wrap justify-center gap-4">
                {live.participants.map((person) => (
                  <li key={person.id} className="flex w-20 flex-col items-center gap-1.5">
                    <Avatar
                      id={person.id}
                      name={person.displayName}
                      src={person.avatarUrl}
                      size="lg"
                      online={online ? online.has(person.id) : undefined}
                    />
                    <span className="w-full truncate text-xs">{person.displayName}</span>
                  </li>
                ))}
              </ul>
            )}
            {config.data?.enabled === false ? (
              <p className="text-sm text-muted">
                {t('Las llamadas no están activadas en este servidor.')}
              </p>
            ) : !canIn(channel, 'connect') ? (
              <p className="text-sm text-muted">
                {t('No tienes permiso para entrar en este canal.')}
              </p>
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                <Button icon={<Mic className="size-4" />} onClick={() => void join(false)}>
                  {t('Entrar', { context: 'call' })}
                </Button>
                {canIn(channel, 'speak') && (
                  <Button
                    variant="secondary"
                    icon={<Video className="size-4" />}
                    onClick={() => void join(true)}
                  >
                    {t('Entrar con cámara')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
